'use client';

import { useEffect, useState } from 'react'; // useState still used for filteredTourSteps
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import type { RootState } from '../../redux/root-reducer';
import { campaignWorkspaceSelector } from '../../redux/buzzbridge/selectors';
import { showParticipantWorkspaceGuideSelector } from '../../selectors/selectors';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { BRAND_LUXE_PRIMARY } from './brandDashboardTokens';
import { CampaignWorkspaceShell } from './campaignDetail/CampaignWorkspaceShell';
import { Tutorial } from '../../components/Tutorial/Tutorial';
import { participantWorkspaceTutorialSteps } from '../../components/EsSettings/settings.data';
import type { ITutorialState } from '../../components/Tutorial/Tutorial';

export default function BrandCampaignParticipantPage() {
    const { campaignId, participantId } = useParams<{ campaignId: string; participantId: string }>();
    const currentUserId = useSelector((s: RootState) => s.user.userProfile?._id);
    const showWorkspaceTour = useSelector(showParticipantWorkspaceGuideSelector);
    const [filteredTourSteps, setFilteredTourSteps] = useState<ITutorialState | null>(null);
    const workspace = useSelector(campaignWorkspaceSelector);

    useEffect(() => {
        if (!workspace || !showWorkspaceTour) return;
        const timer = setTimeout(() => {
            const visible = participantWorkspaceTutorialSteps.steps.filter(step => {
                const el = document.querySelector(step.target as string);
                if (!el) return false;
                const rect = el.getBoundingClientRect();
                return rect.width > 0 && rect.height > 0;
            });
            setFilteredTourSteps({ ...participantWorkspaceTutorialSteps, steps: visible });
        }, 400);
        return () => clearTimeout(timer);
    }, [workspace, showWorkspaceTour]);

    const campaignDetailPath = ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaignId || '');
    const acceptedCreatorsPath = ROUTES.BRAND_CAMPAIGN_ACCEPTED_CREATORS.replace(':campaignId', campaignId || '');
    const creatorName =
        workspace?.creator?.influencer?.name?.trim() ||
        (workspace?.creator?.user && 'userName' in workspace.creator.user ? workspace.creator.user.userName : null) ||
        'Creator';
    const title =
        workspace?.campaign?.title != null ? `${creatorName} | ${workspace.campaign.title}` : 'Campaign workspace';

    return (
        <MetaTags title={title} description="Participant detail">
            {filteredTourSteps && filteredTourSteps.steps.length > 0 && (
                <Tutorial
                    stepsDetails={filteredTourSteps}
                    configField="show_participant_workspace_guide"
                    onEnd={() => setFilteredTourSteps(null)}
                />
            )}
            <CampaignWorkspaceShell
                perspective="brand"
                participantId={participantId || ''}
                currentUserId={currentUserId}
                renderBreadcrumbs={({ workspace: ws }) => {
                    const crumbName =
                        ws?.creator?.influencer?.name?.trim() ||
                        (ws?.creator?.user && 'userName' in ws.creator.user ? ws.creator.user.userName : null) ||
                        creatorName;
                    return (
                        <Breadcrumbs
                            aria-label="breadcrumb"
                            sx={{
                                alignSelf: 'flex-start',
                                '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.35)' },
                            }}>
                            <Link
                                component={RouterLink}
                                to={ROUTES.BRAND_CAMPAIGNS}
                                underline="hover"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: 'rgba(255,255,255,0.75)',
                                    '&:hover': { color: BRAND_LUXE_PRIMARY },
                                }}>
                                Campaigns
                            </Link>
                            <Link
                                component={RouterLink}
                                to={campaignDetailPath}
                                underline="hover"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: 'rgba(255,255,255,0.75)',
                                    '&:hover': { color: BRAND_LUXE_PRIMARY },
                                }}>
                                {ws?.campaign?.title || 'Campaign'}
                            </Link>
                            <Link
                                component={RouterLink}
                                to={acceptedCreatorsPath}
                                underline="hover"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: 'rgba(255,255,255,0.75)',
                                    '&:hover': { color: BRAND_LUXE_PRIMARY },
                                }}>
                                Accepted Creators
                            </Link>
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY }}>
                                {crumbName}
                            </Typography>
                        </Breadcrumbs>
                    );
                }}
            />
        </MetaTags>
    );
}
